import {
  ChatMessageWithParts,
  ContentType,
  TMessageContentPart,
} from "src/views/assistant/types";

import { memo, useMemo } from "react";
import { useChatContext } from "../context/ChatContext";
import { resolveEffectivePartType } from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";
import Artifact from "./artifacts/Artifact";
import AssistantText from "./AssistantText";
import ThinkingContent from "./thoughts/ThoughtsDropdown";
import {
  isInteractiveToolResponse,
  isToolResponseTextComplete,
  parseToolResponseText,
} from "./artifacts/toolStatusUtils";

/** Whether a transcript-only reader is shown a part: prose or an interactive result. */
export const isReaderPart = (
  part: TMessageContentPart,
  isChatConnected: boolean,
): boolean => {
  const type = resolveEffectivePartType(part);
  const text = typeof part.text === "string" ? part.text : "";
  if (type === ContentType.TEXT) return Boolean(text.trim());
  return (
    type === ContentType.TOOL_RESPONSE &&
    isToolResponseTextComplete(text, isChatConnected) &&
    isInteractiveToolResponse(parseToolResponseText(text))
  );
};

type AssistantPartProps = {
  chatMessage: ChatMessageWithParts;
  part: TMessageContentPart;
  partIndex: number;
  messageIndex: number;
  isChatConnected: boolean;
  isChatMessageStreamStopped?: boolean;
};

export const AssistantPart = memo(
  ({
    chatMessage,
    part,
    partIndex,
    messageIndex,
    isChatConnected,
    isChatMessageStreamStopped,
  }: AssistantPartProps) => {
    if (!part) return null;

    const { CustomAssistantText, CustomAssistantTextProps, transcriptOnly } =
      useChatContext();
    const text = typeof part.text === "string" ? part.text : "";

    // Safety net: if a TEXT part's text is actually a JSON tool payload,
    // render it as the correct tool component instead of Markdown.
    // This catches any case where upstream normalization missed it.
    const effectivePart = useMemo(() => {
      const effectiveType = resolveEffectivePartType(part);
      return effectiveType === part.type
        ? part
        : { ...part, type: effectiveType };
    }, [part]);

    // A transcript-only reader sees the reply, never the agent's working.
    if (
      transcriptOnly &&
      effectivePart.type !== ContentType.TEXT &&
      !isReaderPart(part, isChatConnected)
    )
      return null;

    switch (effectivePart.type) {
      case ContentType.THINK:
        return (
          <ThinkingContent text={text} isChatConnected={isChatConnected} />
        );

      case ContentType.TOOL_CALL:
      case ContentType.TOOL_RESPONSE:
        return (
          <Artifact
            part={effectivePart}
            partIndex={partIndex}
            isChatConnected={isChatConnected}
            isChatMessageStreamStopped={isChatMessageStreamStopped}
            chatMessage={chatMessage}
          />
        );

      case ContentType.TEXT:
        // Tool-sentinel separators and parser splits often create pure whitespace
        // text parts, which render as large blank gaps in the chat UI.
        if (!text.trim()) return null;
        return (
          <>
            {CustomAssistantText ? (
              <CustomAssistantText
                chatMessage={chatMessage}
                content={text}
                messageIndex={messageIndex}
                {...CustomAssistantTextProps}
              />
            ) : (
              <AssistantText chatMessage={text} />
            )}
          </>
        );

      default:
        // Unknown content type — don't render to avoid leaking raw data
        return null;
    }
  },
);
